import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TransportProvider } from '@connectrpc/connect-query'
import { connectTransport } from '../../lib/connect-transport'
import { smartRetry } from '../../lib/query-utils'
import { isExpectedError, reportError } from '../../lib/error-reporting'

// Create a global query client instance that can be accessed outside of React components
// Expected, user-actionable errors are not reported — they are handled
// business outcomes, not bugs. See isExpectedError in error-reporting.
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      if (!isExpectedError(err)) reportError(err, { kind: 'query' })
    },
  }),
  mutationCache: new MutationCache({
    onError: (err) => {
      if (!isExpectedError(err)) reportError(err, { kind: 'mutation' })
    },
  }),
  defaultOptions: {
    queries: {
      // Don't refetch on window focus - too aggressive for a CRM app
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect - let user manually refresh if needed
      refetchOnReconnect: false,
      // Consider data fresh for 5 minutes by default
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Smart retry: skip retries on 404/401/403/400, retry up to 3x for transient errors
      retry: smartRetry,
    },
  },
})

export function getContext() {
  return {
    queryClient,
  }
}

export function Provider({
  children,
  queryClient,
}: {
  children: React.ReactNode
  queryClient: QueryClient
}) {
  return (
    <TransportProvider transport={connectTransport}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </TransportProvider>
  )
}
